import { Injectable } from '@angular/core';
import type {
  IStraforKutuk, IHedefParca, IMontajPlani,
  IBlokYerlestirme, IYapistamaYuz, IKalinlikKatman,
} from '../interfaces/strafor.interface';

/**
 * Montaj Hesaplama Servisi
 *
 * Standart kütüklerden istenen boyutta parça OLUŞTURMA algoritması.
 * Kural: X,Y ekseni serbestçe kesilebilir. Z ekseni (kalınlık) için
 * farklı standart kalınlıklardaki kütükler üst üste yığılır - kesim yok.
 *
 * Geçici kütükler (artıklar) öncelikli kullanılır → maliyet tasarrufu.
 */
@Injectable({ providedIn: 'root' })
export class MontajHesaplamaService {

  /**
   * Ana hesaplama: Tüm olası montaj seçeneklerini bulur.
   * Her dizilim (normal/döndürülmüş) × her kalınlık kombinasyonu denenir.
   * Geçici kütükler her pozisyonda standart bloklardan ÖNCE kontrol edilir.
   */
  hesapla(hedef: IHedefParca, kutukler: IStraforKutuk[], geciciKutukler: IStraforKutuk[] = [], geciciOncelikli = false): IMontajPlani[] {
    if (!kutukler.length || hedef.en <= 0 || hedef.boy <= 0 || hedef.yukseklik <= 0) return [];

    // Kalınlık → kütük eşleştirme (en ucuz olanı seç)
    const kutukMap = new Map<number, IStraforKutuk>();
    for (const k of kutukler) {
      if (!kutukMap.has(k.yukseklik) ||
          (k.birimFiyat || 0) < (kutukMap.get(k.yukseklik)!.birimFiyat || 0)) {
        kutukMap.set(k.yukseklik, k);
      }
    }

    const kalinliklar = [...kutukMap.keys()].sort((a, b) => b - a);
    if (!kalinliklar.length) return [];

    // Ana kütük ayak izi (en × boy)
    const temel = kutukler[0];

    // Kalınlık kombinasyonlarını bul
    const kombinasyonlar = this.kalinlikKombinasyonlariBul(hedef.yukseklik, kalinliklar);

    // Her dizilim × kombinasyon için plan oluştur
    const secenekler: IMontajPlani[] = [];

    for (const dizilim of ['normal', 'dondurulmus'] as const) {
      const kEn = dizilim === 'normal' ? temel.en : temel.boy;
      const kBoy = dizilim === 'normal' ? temel.boy : temel.en;

      const adetX = Math.ceil(hedef.en / kEn);
      const adetY = Math.ceil(hedef.boy / kBoy);

      for (const kombi of kombinasyonlar) {
        const plan = this.planOlustur(hedef, dizilim, kEn, kBoy, adetX, adetY, kombi, kutukMap, geciciKutukler, geciciOncelikli);
        if (plan) secenekler.push(plan);
      }
    }

    // ═══ GEÇİCİ ÖNCELİKLİ: Geçici kütük boyutlarına göre ek grid denemeleri ═══
    if (geciciOncelikli && geciciKutukler.length > 0) {
      const denenmis = new Set<string>();
      for (const g of geciciKutukler) {
        if (g.adet <= 0) continue;
        const dims = [g.en, g.boy, g.yukseklik];
        // 6 oryantasyon: her boyut çifti en/boy olarak denensin
        for (const [a, b] of [[0,1],[1,0],[0,2],[2,0],[1,2],[2,1]] as [number,number][]) {
          const kEn = dims[a];
          const kBoy = dims[b];
          if (kEn < 10 || kBoy < 10) continue;
          const key = `${kEn}_${kBoy}`;
          if (denenmis.has(key)) continue;
          denenmis.add(key);

          const adetX = Math.ceil(hedef.en / kEn);
          const adetY = Math.ceil(hedef.boy / kBoy);

          for (const kombi of kombinasyonlar) {
            const plan = this.planOlustur(hedef, 'normal', kEn, kBoy, adetX, adetY, kombi, kutukMap, geciciKutukler, true);
            if (plan) secenekler.push(plan);
          }
        }
      }
    }

    // Benzersizleştir (aynı adetX×adetY + aynı kalınlık = aynı sonuç)
    const benzersiz = new Map<string, IMontajPlani>();
    for (const s of secenekler) {
      const key = `${s.adetX}x${s.adetY}_${s.kalinlikAciklama}`;
      if (!benzersiz.has(key) || s.skor < benzersiz.get(key)!.skor) {
        benzersiz.set(key, s);
      }
    }

    return [...benzersiz.values()].sort((a, b) => a.skor - b.skor).slice(0, 20);
  }

  /**
   * Kalınlık kombinasyonlarını bul (DFS).
   * Hedef yüksekliğe TAM ulaşan kombinasyonlar öncelikli.
   * Z'de kesim yok, sadece TAM kalınlık bloklar kullanılır.
   */
  private kalinlikKombinasyonlariBul(hedef: number, kalinliklar: number[]): number[][] {
    const sorted = [...kalinliklar].sort((a, b) => b - a);
    const sonuclar: { katmanlar: number[]; fazla: number }[] = [];
    const enInce = sorted[sorted.length - 1];
    const maxKatman = Math.min(Math.ceil(hedef / enInce), 10);

    const ara = (kalan: number, idx: number, mevcut: number[]) => {
      if (kalan === 0) {
        sonuclar.push({ katmanlar: [...mevcut], fazla: 0 });
        return;
      }
      if (kalan < 0) return;
      if (mevcut.length >= maxKatman || sonuclar.length >= 500) return;

      const kalanKatman = maxKatman - mevcut.length;
      if (kalan > kalanKatman * sorted[idx]) return;

      for (let i = idx; i < sorted.length; i++) {
        mevcut.push(sorted[i]);
        ara(kalan - sorted[i], i, mevcut);
        mevcut.pop();
      }
    };

    ara(hedef, 0, []);

    // Tam eşleşme yoksa, en yakın aşanları da dene
    if (sonuclar.length === 0) {
      const araFazlali = (kalan: number, idx: number, mevcut: number[]) => {
        if (kalan <= 0) {
          sonuclar.push({ katmanlar: [...mevcut], fazla: -kalan });
          return;
        }
        if (mevcut.length >= maxKatman || sonuclar.length >= 300) return;
        const kalanK = maxKatman - mevcut.length;
        if (kalan > kalanK * sorted[idx]) return;

        for (let i = idx; i < sorted.length; i++) {
          mevcut.push(sorted[i]);
          araFazlali(kalan - sorted[i], i, mevcut);
          mevcut.pop();
        }
      };
      araFazlali(hedef, 0, []);
    }

    sonuclar.sort((a, b) => {
      if (a.fazla !== b.fazla) return a.fazla - b.fazla;
      return a.katmanlar.length - b.katmanlar.length;
    });

    const seen = new Set<string>();
    const unique: number[][] = [];
    for (const s of sonuclar) {
      const key = s.katmanlar.join('+');
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(s.katmanlar);
        if (unique.length >= 12) break;
      }
    }

    return unique;
  }

  /**
   * Geçici havuzdan en uygun kütüğü bul.
   * Hem normal hem döndürülmüş oryantasyon denenir.
   * En az fire bırakan seçilir.
   */
  /**
   * Geçici havuzdan en uygun kütüğü bul (6 oryantasyon denenir).
   * Geçici kütükler yatay (X/Y kenar dolgusu) olarak da kullanılabilir,
   * sadece Z kalınlık eşleşmesi zorunlu değil.
   */
  private enUygunGeciciBul(
    havuz: IStraforKutuk[],
    neededEn: number, neededBoy: number, neededYuk: number,
  ): { ref: IStraforKutuk; en: number; boy: number; yuk: number } | null {
    let best: { ref: IStraforKutuk; en: number; boy: number; yuk: number; waste: number } | null = null;

    for (const g of havuz) {
      if (g.adet <= 0) continue;

      const d = [g.en, g.boy, g.yukseklik];
      // 6 oryantasyon: her boyut en/boy/yük olarak denensin
      for (const [a, b, c] of [[0,1,2],[0,2,1],[1,0,2],[1,2,0],[2,0,1],[2,1,0]]) {
        if (d[a] >= neededEn && d[b] >= neededBoy && d[c] >= neededYuk) {
          const waste = d[a] * d[b] * d[c] - neededEn * neededBoy * neededYuk;
          if (!best || waste < best.waste) {
            best = { ref: g, en: d[a], boy: d[b], yuk: d[c], waste };
          }
        }
      }
    }

    return best;
  }

  /**
   * Kenar parçaları için en uygun kütüğü bul (tüm oryantasyonlar denenir).
   * Örn: 40mm genişliğindeki şerit için 50mm kütük döndürülerek kullanılabilir.
   * Maliyet öncelikli, sonra fire karşılaştırması yapılır.
   */
  private kenarIcinEnUygunKutuk(
    kutukMap: Map<number, IStraforKutuk>,
    nEn: number, nBoy: number, nYuk: number,
    stdFire: number, stdMaliyet: number,
  ): { en: number; boy: number; yuk: number; maliyet: number } | null {
    let best: { en: number; boy: number; yuk: number; fire: number; maliyet: number } | null = null;

    for (const [, kutuk] of kutukMap) {
      const d = [kutuk.en, kutuk.boy, kutuk.yukseklik];
      // 6 oryantasyon: [en, boy, yük] olarak hangi fiziksel boyut kullanılsın
      for (const [a, b, c] of [[0,1,2],[0,2,1],[1,0,2],[1,2,0],[2,0,1],[2,1,0]]) {
        if (d[a] >= nEn && d[b] >= nBoy && d[c] >= nYuk) {
          const fire = d[a] * d[b] * d[c] - nEn * nBoy * nYuk;
          const maliyet = kutuk.birimFiyat || 0;
          if (!best || maliyet < best.maliyet || (maliyet === best.maliyet && fire < best.fire)) {
            best = { en: d[a], boy: d[b], yuk: d[c], fire, maliyet };
          }
        }
      }
    }

    // Sadece gerçek iyileşme varsa döndür
    if (best && (best.maliyet < stdMaliyet || best.fire < stdFire * 0.7)) {
      return best;
    }
    return null;
  }

  /**
   * Tek bir montaj planı oluştur.
   * Geçici kütükler her pozisyonda standart bloklardan ÖNCE kontrol edilir.
   */
  private planOlustur(
    hedef: IHedefParca,
    dizilim: 'normal' | 'dondurulmus',
    kutukEn: number, kutukBoy: number,
    adetX: number, adetY: number,
    kalinliklar: number[],
    kutukMap: Map<number, IStraforKutuk>,
    geciciKutukler: IStraforKutuk[] = [],
    geciciOncelikli = false,
  ): IMontajPlani | null {

    // Katmanlar oluştur
    const katmanlar: IKalinlikKatman[] = [];
    let zPoz = 0;

    for (const k of kalinliklar) {
      const kutuk = kutukMap.get(k);
      if (!kutuk) return null;

      katmanlar.push({
        kutukId: kutuk.id,
        kutukIsim: kutuk.isim,
        kalinlik: k,
        gercekKalinlik: k,
        kesilecekMi: false,
        kesimMiktari: 0,
        zPozisyon: zPoz,
        birimFiyat: kutuk.birimFiyat || 0,
        renk: kutuk.renk || '#ccc',
      });

      zPoz += k;
    }

    const toplamKalinlik = kalinliklar.reduce((s, v) => s + v, 0);
    const fazlaKalinlik = toplamKalinlik - hedef.yukseklik;

    // Geçici havuz (clone adet for tracking)
    const geciciHavuz = geciciKutukler.map(k => ({ ...k }));

    // Bloklar oluştur
    const bloklar: IBlokYerlestirme[] = [];
    let toplamMaliyet = 0;
    let kesimSayisi = 0;
    let tamBlokSay = 0;
    let yapistamaAlani = 0;
    let kullanilanHacim = 0;
    let geciciKullanilanAdet = 0;
    let geciciTasarruf = 0;

    for (let iz = 0; iz < katmanlar.length; iz++) {
      const kat = katmanlar[iz];

      for (let ix = 0; ix < adetX; ix++) {
        for (let iy = 0; iy < adetY; iy++) {
          const pozX = ix * kutukEn;
          const pozY = iy * kutukBoy;

          const gercekEn = Math.min(kutukEn, hedef.en - pozX);
          const gercekBoy = Math.min(kutukBoy, hedef.boy - pozY);
          const gercekYuk = kat.kalinlik;

          // Yapıştırma yüzleri
          const yapistama: IYapistamaYuz[] = [];
          if (ix > 0) {
            const alan = gercekBoy * gercekYuk;
            yapistama.push({ eksen: 'x', yon: '-', alan });
            yapistamaAlani += alan;
          }
          if (iy > 0) {
            const alan = gercekEn * gercekYuk;
            yapistama.push({ eksen: 'y', yon: '-', alan });
            yapistamaAlani += alan;
          }
          if (iz > 0) {
            const alan = gercekEn * gercekBoy;
            yapistama.push({ eksen: 'z', yon: '-', alan });
            yapistamaAlani += alan;
          }

          // ═══ GEÇİCİ KÜTÜK KONTROLü (ÖNCELİKLİ) ═══
          const gecici = this.enUygunGeciciBul(geciciHavuz, gercekEn, gercekBoy, gercekYuk);

          if (gecici) {
            // Geçici kütük kullan (zaten elimizde var, maliyet 0)
            gecici.ref.adet--;
            geciciKullanilanAdet++;
            geciciTasarruf += kat.birimFiyat;

            const gEn = gecici.en;
            const gBoy = gecici.boy;
            const gYuk = gecici.yuk;

            const kesilecekX = gEn > gercekEn;
            const kesilecekY = gBoy > gercekBoy;
            const kesilecekZ = gYuk > gercekYuk;
            const kesilecek = kesilecekX || kesilecekY || kesilecekZ;

            if (kesilecek) kesimSayisi++;
            else tamBlokSay++;

            kullanilanHacim += gEn * gBoy * gYuk;
            // Maliyet EKLENMEZ (zaten elimizde)

            bloklar.push({
              id: `b-${ix}-${iy}-${iz}`,
              katmanIdx: iz,
              gridPoz: { ix, iy, iz },
              pozisyon: { x: pozX, y: pozY, z: kat.zPozisyon },
              tamBoyut: { en: gEn, boy: gBoy, yukseklik: gYuk },
              gercekBoyut: { en: gercekEn, boy: gercekBoy, yukseklik: gercekYuk },
              kesilecekMi: kesilecek,
              kesimMiktari: {
                en: kesilecekX ? gEn - gercekEn : 0,
                boy: kesilecekY ? gBoy - gercekBoy : 0,
                yukseklik: kesilecekZ ? gYuk - gercekYuk : 0,
              },
              yapistamaYuzleri: yapistama,
              katmanRenk: kat.renk,
              geciciKutukId: gecici.ref.id,
              geciciKutukIsim: gecici.ref.isim,
            });

          } else {
            // ═══ STANDART BLOK ═══
            let bEn = kutukEn;
            let bBoy = kutukBoy;
            let bYuk = kat.kalinlik;
            let bMaliyet = kat.birimFiyat;

            const stdKesilecekX = gercekEn < kutukEn;
            const stdKesilecekY = gercekBoy < kutukBoy;

            // ═══ KENAR OPTİMİZASYONU ═══
            // Kesim gereken parçalarda daha uygun kütük var mı? (döndürülmüş kullanım)
            // Örn: 40mm genişliğindeki şerit için 50mm kütük döndürülerek kullanılır
            if (stdKesilecekX || stdKesilecekY) {
              const stdFire = kutukEn * kutukBoy * kat.kalinlik - gercekEn * gercekBoy * gercekYuk;
              const alt = this.kenarIcinEnUygunKutuk(kutukMap, gercekEn, gercekBoy, gercekYuk, stdFire, kat.birimFiyat);
              if (alt) {
                bEn = alt.en;
                bBoy = alt.boy;
                bYuk = alt.yuk;
                bMaliyet = alt.maliyet;
              }
            }

            const kesilecekX = gercekEn < bEn;
            const kesilecekY = gercekBoy < bBoy;
            const kesilecekZ = gercekYuk < bYuk;
            const kesilecek = kesilecekX || kesilecekY || kesilecekZ;

            if (kesilecek) kesimSayisi++;
            else tamBlokSay++;

            kullanilanHacim += bEn * bBoy * bYuk;
            toplamMaliyet += bMaliyet;

            bloklar.push({
              id: `b-${ix}-${iy}-${iz}`,
              katmanIdx: iz,
              gridPoz: { ix, iy, iz },
              pozisyon: { x: pozX, y: pozY, z: kat.zPozisyon },
              tamBoyut: { en: bEn, boy: bBoy, yukseklik: bYuk },
              gercekBoyut: { en: gercekEn, boy: gercekBoy, yukseklik: gercekYuk },
              kesilecekMi: kesilecek,
              kesimMiktari: {
                en: kesilecekX ? bEn - gercekEn : 0,
                boy: kesilecekY ? bBoy - gercekBoy : 0,
                yukseklik: kesilecekZ ? bYuk - gercekYuk : 0,
              },
              yapistamaYuzleri: yapistama,
              katmanRenk: kat.renk,
            });
          }
        }
      }
    }

    const hedefHacim = hedef.en * hedef.boy * hedef.yukseklik;
    const fireHacim = kullanilanHacim - hedefHacim;
    const fireOrani = kullanilanHacim > 0 ? (fireHacim / kullanilanHacim) * 100 : 0;

    const kalinlikAciklama = kalinliklar.join('+') + 'mm';

    // Skor: düşük = iyi
    // Geçici kullanımı BONUS verir (skor düşürür)
    // geciciOncelikli modda bonus 5x, standart blok cezası eklenir
    const geciciBonus = geciciOncelikli ? 50 : 10;
    const stdBlokCeza = geciciOncelikli ? (bloklar.length - geciciKullanilanAdet) * 5 : 0;
    const skor = Math.max(0, fireOrani) * 3
      + katmanlar.length * 2
      + bloklar.length * 0.5
      + toplamMaliyet * 0.005
      + fazlaKalinlik * 0.5
      - geciciKullanilanAdet * geciciBonus
      + stdBlokCeza;

    return {
      hedef,
      dizilim,
      kutukEn,
      kutukBoy,
      adetX,
      adetY,
      katmanlar,
      bloklar,
      toplamBlok: bloklar.length,
      fireOrani: Math.round(Math.max(0, fireOrani) * 100) / 100,
      hedefHacim,
      kullanilanHacim,
      fireHacim: Math.max(0, fireHacim),
      toplamMaliyet,
      toplamKesimSayisi: kesimSayisi,
      tamBlokSayisi: tamBlokSay,
      toplamYapistamaAlani: yapistamaAlani,
      kalinlikAciklama,
      skor,
      kullanilanGeciciAdet: geciciKullanilanAdet,
      geciciTasarruf,
    };
  }
}
